import React, { memo, useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  JobExternalStakeholdersPopupComponentDefaultProps,
  JobExternalStakeholdersPopupComponentPropTypes,
} from 'src/components/JobExternalStakeholdersPopup/propTypes';
import {
  JobExternalStakeholdersPopupType,
} from 'src/components/CapBox/constants';
import { Popup } from 'src/components/Popup';
import { getAffiliationOptionsList } from 'src/components/JobForm/utils';

import { useCreateJobExternalStakeholder, useCreateJobCompanyContact } from 'src/hooks/jobExternalStakeholder';
import { useJobCompanyContacts } from 'src/hooks/companyContact/useJobCompanyContacts';
import FormRow from 'src/components/Form/FormRow';
import JobAddNewContactForm from 'src/components/JobExternalStakeholdersPopup/JobAddNewContactForm';
import s from 'src/components/JobForm/JobForm.styles';
import Styles from 'src/components/JobExternalStakeholdersPopup/JobExternalStakeholdersPopup.styles';

const JobExternalStakeholdersPopupComponent = ({
  companyTypes,
  disabled,
  excludedUsersIds,
  job,
  onClose,
  serviceType,
  onSubmit,
  type,
}) => {
  const { t } = useTranslation();
  const { id: jobId } = job;
  const submitButtonRef = useRef(null);

  const affiliationOptionsList = useMemo(() => getAffiliationOptionsList(job, companyTypes), [job]);

  const [formType, setFormType] = useState(type);
  const [selectedContactsIds, setSelectedContactsIds] = useState([]);
  const [selectedAffiliation, setSelectedAffiliation] = useState(null);
  const [inputFirstName, setInputFirstName] = useState(null);
  const [inputLastName, setInputLastName] = useState(null);
  const [inputEmail, setInputEmail] = useState(null);
  const [newContactFormError, setNewContactFormError] = useState(true);

  const [getJobCompanyContacts, {
    loading: isFetchingJobCompanyContacts,
    data,
  }] = useJobCompanyContacts(jobId, serviceType);

  useEffect(() => {
    getJobCompanyContacts();
  }, []);

  const jobCompanyContacts = data?.jobCompanyContacts?.filter(manager => !excludedUsersIds.includes(parseInt(manager.id, 10))) || [];

  const companyTypesRelatedToJob = (contact) => {
    const contactCompanyId = contact.companyId;
    const companyTypeIds = job.jobCompanies?.filter(c => c.company.id === contactCompanyId)?.map(c => c.companyTypeId);
    const types = contact.company.currentVersion.companyTypes.filter(tp => companyTypeIds.includes(tp.id));
    return types.map(tp => tp.currentVersion.name).join(', ');
  };

  const {
    isLoading: isCreatingJobExternalStakeholder,
    createJobExternalStakeholderCallback: createJobExternalStakeholder,
  } = useCreateJobExternalStakeholder();

  const {
    isLoading: isCreatingJobCompanyContact,
    createJobCompanyContactCallback: createJobCompanyContact,
  } = useCreateJobCompanyContact();

  const isLoading = isFetchingJobCompanyContacts || isCreatingJobExternalStakeholder || isCreatingJobCompanyContact;

  const onSubmitPopup = async () => {
    let response = null;

    if (formType === JobExternalStakeholdersPopupType.AddExisting) {
      response = await createJobExternalStakeholder({
        jobId,
        contactIds: selectedContactsIds,
        serviceType,
      });
      if (response) {
        await onSubmit();
      }
      onClose();
    } else if (formType === JobExternalStakeholdersPopupType.AddNew) {

      if (newContactFormError) {
        return;
      }

      response = await createJobCompanyContact({
        companyId: selectedAffiliation.value,
        firstName: inputFirstName,
        lastName: inputLastName,
        email: inputEmail,
      });
      if (response) {
        await onSubmit();
      }
      getJobCompanyContacts();
      setFormType(JobExternalStakeholdersPopupType.AddExisting);
    }

  };

  const onCheckboxChange = contactId => {
    if (selectedContactsIds.includes(contactId)) {
      setSelectedContactsIds(selectedContactsIds.filter(el => el !== contactId));
    } else {
      setSelectedContactsIds([...selectedContactsIds, contactId]);
    }
  };

  const closePop = () => {
    if (formType === JobExternalStakeholdersPopupType.AddExisting) {
      onClose();
    } else if (formType === JobExternalStakeholdersPopupType.AddNew) {
      setFormType(JobExternalStakeholdersPopupType.AddExisting);
    }
  };
  return (
    <Popup
      customTitle={(
        <Styles.PopupHeaderWrapper>
          {formType === JobExternalStakeholdersPopupType.AddExisting ?
            <>
              <s.Title>{t('ADD_USER_FROM_EXISTING_CONTACT')}</s.Title>
              <Styles.Button
                disabled={disabled || isLoading}
                light-blue
                onClick={() => setFormType(JobExternalStakeholdersPopupType.AddNew)}
                outline
                type="button"
              >
                {t('NEW_CONTACT')}
              </Styles.Button>
            </>
            : <s.Title>{t('NEW_CONTACT')}</s.Title>
          }
        </Styles.PopupHeaderWrapper>
      )}
      isCloseHidden
      onClose={onClose}
      withFlex
      withOverflow
      >
      <>
        <FormRow spaced withScroll>
          {formType === JobExternalStakeholdersPopupType.AddExisting && jobCompanyContacts.map(contact => contact.currentVersion?.workEmail && (
            <Styles.RowWrapper key={contact.id}>
              <Styles.RowCell name>
                {`${contact.currentVersion.firstName} ${contact.currentVersion.lastName}`}
              </Styles.RowCell>
              <Styles.RowCell email>
                {contact.currentVersion.workEmail}
              </Styles.RowCell>
              <Styles.RowCell type>
                {companyTypesRelatedToJob(contact)}
              </Styles.RowCell>
              <Styles.RowCell checkbox>
                <Styles.Checkbox
                  disabled={disabled || isLoading}
                  id={`contact-${contact.id}`}
                  onChange={() => onCheckboxChange(contact.id)}
                  value={selectedContactsIds.includes(contact.id)}
                />
              </Styles.RowCell>
            </Styles.RowWrapper>
          ))}
          {formType === JobExternalStakeholdersPopupType.AddNew &&
          (<JobAddNewContactForm
            affiliationOptionsList={affiliationOptionsList}
            disabled={disabled}
            inputEmail={''}
            inputFirstName={''}
            inputLastName={''}
            isLoading={isLoading}
            selectedAffiliation={null}
            setInputEmail={setInputEmail}
            setInputFirstName={setInputFirstName}
            setInputLastName={setInputLastName}
            setNewContactFormError={setNewContactFormError}
            setSelectedAffiliation={setSelectedAffiliation}
          />)}
        </FormRow>
        <Styles.BottomWrapper>
          <Styles.BottomInfoWrapper>
            {formType === JobExternalStakeholdersPopupType.AddExisting && !selectedContactsIds.length && (
              <span>{t('NO_USER_SELECTED')}</span>
            )}
          </Styles.BottomInfoWrapper>

          <Styles.ButtonsWrapper>
            <Styles.Button
              disabled={disabled || isLoading}
              light-blue
              onClick={closePop}
              outline
              type="button"
            >
              {t('CANCEL')}
            </Styles.Button>
            <Styles.Button
              disabled={disabled || isLoading || (formType === JobExternalStakeholdersPopupType.AddExisting && !selectedContactsIds.length)}
              light-blue
              onClick={onSubmitPopup}
              ref={submitButtonRef}
              solid
              type="button"
            >
              {t('ADD')}
            </Styles.Button>
          </Styles.ButtonsWrapper>
        </Styles.BottomWrapper>
      </>
    </Popup>
  );
};

JobExternalStakeholdersPopupComponent.propTypes = JobExternalStakeholdersPopupComponentPropTypes;
JobExternalStakeholdersPopupComponent.defaultProps = JobExternalStakeholdersPopupComponentDefaultProps;

export const JobExternalStakeholdersPopup = memo(JobExternalStakeholdersPopupComponent);
